import {useCallback} from "react";

import peraWalletManager from "../../core/integrations/pera-wallet/PeraWalletManager";
import {WalletContextReducerAction} from "../util/context/WalletContext.types";
import {
  useSelectedWalletType,
  useWalletContextDispatch
} from "../util/context/WalletContext.utils";
import {WalletType} from "../util/walletTypes";
import {
  getWalletTypeFromWebStorage,
  handleDisconnectSuccessful,
  isPeraWallet,
  isWalletConnect
} from "../util/walletUtils";
import walletConnectManager from "../../core/integrations/wallet-connect/WalletConnect.manager";

function useDisconnectAccount() {
  const walletContextDispatch = useWalletContextDispatch();
  const {walletType} = useSelectedWalletType();

  return useCallback(async () => {
    try {
      await disconnectAccount({
        dispatch: walletContextDispatch,
        walletType,
        shouldIgnoreErrors: false
      });
    } catch (error: any) {
      console.error(error);
    }
  }, [walletContextDispatch, walletType]);
}

/**
 * As `useDisconnectAccount` can only be used under the AppContext.Provider,
 * this function is added to be used when we want to disconnect outside of AppContext.Provider
 */
async function disconnectAccount({
  dispatch,
  walletType,
  shouldIgnoreErrors = true
}: {
  dispatch: React.Dispatch<WalletContextReducerAction>;
  walletType?: WalletType | null;
  shouldIgnoreErrors?: boolean;
}) {
  try {
    const storedWalletType = walletType || getWalletTypeFromWebStorage();

    if (isPeraWallet(storedWalletType) && peraWalletManager.isConnected) {
      await peraWalletManager.disconnect();
    } else if (isWalletConnect(storedWalletType)) {
      await walletConnectManager.disconnect();
    }
  } catch (error: any) {
    console.error(error);
    if (shouldIgnoreErrors) {
      return;
    }
    throw error;
  }

  try {
    handleDisconnectSuccessful(dispatch);
  } catch (error: any) {
    console.error(error);
    if (shouldIgnoreErrors) {
      return;
    }
    throw error;
  }
}

export default useDisconnectAccount;
export {disconnectAccount};
